import React, { useEffect, useMemo, useState } from "react";
import CommonInput from "./CommonInput";
import CommonSelect from "./CommonSelect";
import "./CommonTable.css";

function CommonTable({
  columns = [],
  data = [],
  rowKey = "id",
  tableClassName = "",
  wrapperClassName = "",
  noDataClassName = "",
  emptyMessage = "No data available",
  emptyContent = null,
  getRowKey,
  getRowClassName,
  onRowClick,
  emptyColSpan,
  renderFooter,
  enableSearch = true,
  searchPlaceholder = "Search...",
  getSearchText,
  enablePagination = true,
  initialPageSize = 10,
  pageSizeOptions = [10, 25, 50, 100],
  toolbarClassName = "common-table-toolbar",
  entriesClassName = "common-table-entries",
  searchBoxClassName = "common-table-search",
  searchInputClassName = "",
  searchClearClassName = "common-table-search-clear",
  paginationClassName = "common-table-pagination",
  paginationInfoClassName = "common-table-pagination-info",
  paginationButtonsClassName = "common-table-pagination-buttons",
  paginationButtonClassName = "common-table-pagination-button",
  paginationEllipsisClassName = "common-table-pagination-ellipsis",
}) {
  const [searchValue, setSearchValue] = useState("");
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [pageIndex, setPageIndex] = useState(0);

  const resolvedColSpan = emptyColSpan || columns.length || 1;
  const wrapperClasses = ["common-table-responsive", wrapperClassName]
    .filter(Boolean)
    .join(" ");
  const tableClasses = tableClassName || "common-table";
  const emptyClasses = ["common-table-empty", noDataClassName]
    .filter(Boolean)
    .join(" ");

  const resolveRowKey = (row, index) => {
    if (getRowKey) {
      return getRowKey(row, index);
    }

    if (typeof rowKey === "function") {
      return rowKey(row, index);
    }

    return row?.[rowKey] ?? index;
  };

  const flattenSearchValues = (value) => {
    if (
      value === null ||
      value === undefined ||
      typeof value === "function" ||
      typeof value === "symbol"
    ) {
      return [];
    }

    if (
      typeof value === "string" ||
      typeof value === "number" ||
      typeof value === "boolean"
    ) {
      return [String(value)];
    }

    if (Array.isArray(value)) {
      return value.flatMap((item) => flattenSearchValues(item));
    }

    if (typeof value === "object") {
      return Object.values(value).flatMap((item) => flattenSearchValues(item));
    }

    return [];
  };

  const resolveSearchText = (row) => {
    if (getSearchText) {
      return getSearchText(row);
    }

    const columnSearchText = columns
      .map((column) => {
        if (column.searchValue) {
          return column.searchValue(row);
        }

        if (column.key) {
          return row?.[column.key];
        }

        return "";
      })
      .filter(Boolean)
      .join(" ");
    const rowSearchText = flattenSearchValues(row).join(" ");

    return [columnSearchText, rowSearchText].filter(Boolean).join(" ");
  };

  const filteredData = (() => {
    if (!enableSearch || !searchValue.trim()) {
      return data;
    }

    const normalizedSearch = searchValue.trim().toLowerCase();
    return data.filter((row) =>
      String(resolveSearchText(row)).toLowerCase().includes(normalizedSearch),
    );
  })();

  const totalRows = filteredData.length;
  const pageCount = enablePagination
    ? Math.max(1, Math.ceil(totalRows / pageSize))
    : 1;
  const safePageIndex = Math.min(pageIndex, Math.max(pageCount - 1, 0));

  useEffect(() => {
    if (safePageIndex !== pageIndex) {
      setPageIndex(safePageIndex);
    }
  }, [pageIndex, safePageIndex]);

  const visibleRows = (() => {
    if (!enablePagination) {
      return filteredData;
    }

    const start = safePageIndex * pageSize;
    return filteredData.slice(start, start + pageSize);
  })();

  const startRow = totalRows === 0 ? 0 : safePageIndex * pageSize + 1;
  const endRow = enablePagination
    ? Math.min((safePageIndex + 1) * pageSize, totalRows)
    : totalRows;

  const pageNumbers = useMemo(() => {
    if (!enablePagination || pageCount <= 1) {
      return [];
    }

    return Array.from({ length: pageCount }, (_, index) => index)
      .filter(
        (index) =>
          index === 0 ||
          index === pageCount - 1 ||
          Math.abs(index - safePageIndex) <= 1,
      )
      .reduce((accumulator, index, currentIndex, array) => {
        if (currentIndex > 0 && index - array[currentIndex - 1] > 1) {
          accumulator.push("...");
        }
        accumulator.push(index);
        return accumulator;
      }, []);
  }, [enablePagination, pageCount, safePageIndex]);

  const renderCell = (column, row, rowIndex, absoluteIndex, columnIndex) => {
    const cellKey = column.id || column.key || `${column.header}-${columnIndex}`;
    const value = column.key ? row?.[column.key] : undefined;
    const cellClassName =
      typeof column.cellClassName === "function"
        ? column.cellClassName(row, rowIndex)
        : column.cellClassName;

    return (
      <td key={cellKey} className={cellClassName}>
        {column.render
          ? column.render(row, rowIndex, value, {
              absoluteIndex,
              pageIndex: safePageIndex,
              pageSize,
            })
          : value ?? column.fallback ?? "N/A"}
      </td>
    );
  };

  return (
    <>
      {(enableSearch || enablePagination) && (
        <div className={toolbarClassName}>
          {enablePagination ? (
            <div className={entriesClassName}>
              <span>Show</span>
              <CommonSelect
                value={pageSize}
                onChange={(event) => {
                  setPageSize(Number(event.target.value));
                  setPageIndex(0);
                }}
                options={pageSizeOptions}
              />
              <span>entries</span>
            </div>
          ) : null}

          {enableSearch ? (
            <div className={searchBoxClassName}>
              <CommonInput
                value={searchValue}
                onChange={(event) => {
                  setSearchValue(event.target.value);
                  setPageIndex(0);
                }}
                placeholder={searchPlaceholder}
                inputClassName={searchInputClassName}
                icon={<i className="fas fa-search"></i>}
                rightAction={
                  searchValue ? (
                    <button
                      type="button"
                      className={searchClearClassName}
                      onClick={() => {
                        setSearchValue("");
                        setPageIndex(0);
                      }}
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  ) : null
                }
              />
            </div>
          ) : null}
        </div>
      )}

      <div className={wrapperClasses}>
        <table className={tableClasses}>
          <thead>
            <tr>
              {columns.map((column) => (
                <th
                  key={column.id || column.key || column.header}
                  className={column.headerClassName}
                >
                  {column.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visibleRows.length > 0 ? (
              visibleRows.map((row, rowIndex) => {
                const absoluteIndex = enablePagination
                  ? safePageIndex * pageSize + rowIndex
                  : rowIndex;

                return (
                  <tr
                    key={resolveRowKey(row, absoluteIndex)}
                    className={getRowClassName?.(row, absoluteIndex)}
                    onClick={onRowClick ? () => onRowClick(row, absoluteIndex) : undefined}
                  >
                    {columns.map((column, columnIndex) =>
                      renderCell(
                        column,
                        row,
                        rowIndex,
                        absoluteIndex,
                        columnIndex,
                      ),
                    )}
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={resolvedColSpan} className={emptyClasses}>
                  {emptyContent || emptyMessage}
                </td>
              </tr>
            )}
          </tbody>
          {renderFooter ? renderFooter() : null}
        </table>
      </div>

      {enablePagination ? (
        <div className={paginationClassName}>
          <span className={paginationInfoClassName}>
            {totalRows === 0
              ? "No records"
              : `Showing ${startRow} to ${endRow} of ${totalRows} entries`}
          </span>
          <div className={paginationButtonsClassName}>
            <button
              type="button"
              className={paginationButtonClassName}
              onClick={() => setPageIndex(0)}
              disabled={safePageIndex === 0}
              title="First"
            >
              {"<<"}
            </button>
            <button
              type="button"
              className={paginationButtonClassName}
              onClick={() => setPageIndex((current) => Math.max(0, current - 1))}
              disabled={safePageIndex === 0}
              title="Previous"
            >
              {"<"}
            </button>
            {pageNumbers.map((item, index) =>
              item === "..." ? (
                <span
                  key={`ellipsis-${index}`}
                  className={paginationEllipsisClassName}
                >
                  ...
                </span>
              ) : (
                <button
                  key={item}
                  type="button"
                  className={`${paginationButtonClassName} ${
                    safePageIndex === item ? "active" : ""
                  }`.trim()}
                  onClick={() => setPageIndex(item)}
                >
                  {item + 1}
                </button>
              ),
            )}
            <button
              type="button"
              className={paginationButtonClassName}
              onClick={() =>
                setPageIndex((current) => Math.min(pageCount - 1, current + 1))
              }
              disabled={safePageIndex >= pageCount - 1 || totalRows === 0}
              title="Next"
            >
              {">"}
            </button>
            <button
              type="button"
              className={paginationButtonClassName}
              onClick={() => setPageIndex(Math.max(pageCount - 1, 0))}
              disabled={safePageIndex >= pageCount - 1 || totalRows === 0}
              title="Last"
            >
              {">>"}
            </button>
          </div>
        </div>
      ) : null}
    </>
  );
}

export default CommonTable;
